import React, { useEffect, useMemo, useState } from "react";
import { organizerListClubsApi, organizerListTablesApi } from "../../api/api";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import StatsFilters, { periodFields } from "./StatsFilters";
import StatsTabs from "./StatsTabs";
import ResumenTab from "./ResumenTab";
import EvolucionTab from "./EvolucionTab";
import HorariosTab from "./HorariosTab";
import CanchasTab from "./CanchasTab";
import TurnosTab from "./TurnosTab";
import { StatsRangeCaption } from "./StatsPrimitives";
import { StatsTooltipProvider } from "./StatsTooltip";
import useCourtStats from "./useCourtStats";
import { bucketSize, daysInRange, resolveComparison, resolvePeriod } from "./statsDates";
import { courtFilterOptions, sportFilterOptions, validCourtSelection } from "./courtStatsMetrics";
import { buildStatsSheets, statsFileName } from "./courtStatsSheets";
import { downloadStatsWorkbook } from "./statsExport";
import "./estadisticas.css";

/* ─────────────────────────────────────────
   EstadisticasCanchas — court statistics of a club (Canchas > Estadísticas).

   Data: GET /api/organizer/clubs/:clubId/stats, called twice in parallel (current range and
   comparison range) by useCourtStats. All the numbers are computed by the back; the pure helpers in
   courtStats*.js only reshape them for display.
   Tabs: Resumen, Evolución y canales, Horarios, Por cancha, Por tipo de turno.
   ───────────────────────────────────────── */

const TABS = [
  { id: "resumen", label: "Resumen" },
  { id: "evolucion", label: "Evolución y canales" },
  { id: "horarios", label: "Horarios" },
  { id: "canchas", label: "Por cancha" },
  { id: "turnos", label: "Por tipo de turno" },
];

const COMPARE_LABELS = { prev: "vs período anterior", yoy: "vs año pasado" };

const INITIAL_FILTERS = { period: "last30", compare: "prev", sport: "all", court: "all" };

export default function EstadisticasCanchas({ today: todayProp }) {
  // "Today" is read once: the ranges must not move under the user's feet while the page is open.
  const [today] = useState(() => todayProp || new Date());

  const [clubs, setClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(true);
  const [clubsError, setClubsError] = useState("");
  const [clubId, setClubId] = useState("");
  const [courts, setCourts] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [exportError, setExportError] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await organizerListClubsApi();
        const list = Array.isArray(res?.data?.info) ? res.data.info : [];
        if (!active) return;
        // A deleted club (status false) does not count, same rule as the Canchas screen.
        const alive = list.filter((club) => club.status !== false);
        setClubs(alive);
        setClubId(alive[0]?._id || "");
      } catch (err) {
        if (active) setClubsError(err?.response?.data?.message || "No pudimos cargar tu club.");
      } finally {
        if (active) setClubsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Courts of the selected club feed the sport and court filters. Losing them only empties the
  // filters; the statistics themselves do not depend on this request.
  useEffect(() => {
    if (!clubId) return undefined;
    let active = true;
    setCourts([]);
    (async () => {
      try {
        const res = await organizerListTablesApi(clubId);
        if (active) setCourts(Array.isArray(res?.data?.info) ? res.data.info : []);
      } catch (_) {
        /* filters stay on "Todos" / "Todas" */
      }
    })();
    return () => {
      active = false;
    };
  }, [clubId]);

  const range = useMemo(() => resolvePeriod(filters.period, today), [filters.period, today]);
  const comparison = useMemo(
    () => resolveComparison(range, filters.period, filters.compare),
    [range, filters.period, filters.compare]
  );

  const { data, loading, error, comparisonFailed } = useCourtStats({
    clubId,
    current: range,
    comparison,
    compareMode: filters.compare,
    sport: filters.sport,
    courtId: filters.court,
  });

  const sportOptions = useMemo(() => sportFilterOptions(courts), [courts]);
  const courtOptions = useMemo(() => courtFilterOptions(courts, filters.sport), [courts, filters.sport]);

  const handleFilterChange = (patch) =>
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      // A court that the new sport excludes (or that no longer exists) must not stay selected.
      return { ...next, court: validCourtSelection(next.court, courts, next.sport) };
    });

  // One workbook (Resumen, Diario, Horarios, Canchas, Tipos de turno) with exactly what is on screen: the loaded
  // responses and the labels of the filters they were requested with.
  const handleExport = () => {
    if (!data) return;
    setExportError(false);
    try {
      const club = clubs.find((c) => c._id === clubId);
      const sheets = buildStatsSheets({
        current: data.current,
        comparison: data.comparison,
        clubName: club?.name || "",
        sportLabel: sportOptions.find((o) => o.value === filters.sport)?.label || "Todos",
        courtLabel: courts.find((c) => c._id === filters.court)?.name || "Todas",
      });
      downloadStatsWorkbook(sheets, statsFileName({ clubName: club?.name, from: data.current.from, to: data.current.to }));
    } catch (_) {
      setExportError(true);
    }
  };

  const handleClubChange = (id) => {
    setClubId(id);
    setFilters((prev) => ({ ...prev, sport: "all", court: "all" }));
  };

  // Club first (only with several clubs), then the shared period/comparison pair, then sport and court.
  const filterFields = [
    ...(clubs.length > 1
      ? [{ key: "club", label: "Club", value: clubId, onChange: handleClubChange, options: clubs.map((club) => ({ value: club._id, label: club.name })) }]
      : []),
    ...periodFields(filters, handleFilterChange),
    { key: "sport", label: "Deporte", value: filters.sport, onChange: (sport) => handleFilterChange({ sport }), options: sportOptions, allLabel: "Todos" },
    { key: "court", label: "Cancha", value: filters.court, onChange: (court) => handleFilterChange({ court }), options: courtOptions, allLabel: "Todas" },
  ];

  if (clubsLoading) return <SpinLoading />;

  if (clubsError) {
    return (
      <div className="zoco-stats">
        <p className="stats-error" role="alert">
          {clubsError}
        </p>
      </div>
    );
  }

  if (!clubId) {
    return (
      <div className="zoco-stats">
        <div className="stats-empty">
          <p>Este negocio todavía no tiene club.</p>
          <p>Configuralo desde el módulo Canchas para empezar a recibir reservas y ver sus estadísticas.</p>
        </div>
      </div>
    );
  }

  const shown = data?.current;
  const compareLabel = COMPARE_LABELS[data?.compareMode] || "";
  const bucket = shown ? bucketSize(daysInRange(shown)) : 1;

  return (
    <div className="zoco-stats">
      <StatsTooltipProvider>
        <StatsFilters fields={filterFields}>
          <button type="button" className="stats-btn stats-btn--dark" onClick={handleExport} disabled={!data || loading}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
            </svg>
            Exportar Excel
          </button>
        </StatsFilters>

        {shown && <StatsRangeCaption shown={shown} comparison={data.comparison} />}
        {exportError && (
          <p className="stats-note" role="status">
            No pudimos generar el Excel. Probá de nuevo.
          </p>
        )}
        {comparisonFailed && (
          <p className="stats-note" role="status">
            No pudimos cargar el período de comparación. Mostramos solo el período elegido.
          </p>
        )}

        <StatsTabs tabs={TABS} label="Secciones de estadísticas" busy={loading} className={`stats-content${loading && shown ? " is-refreshing" : ""}`}>
          {(tab) => {
            if (error) {
              return (
                <p className="stats-error" role="alert">
                  {error}
                </p>
              );
            }
            if (!shown) return loading ? <SpinLoading /> : null;
            if (tab === "resumen") {
              return <ResumenTab current={shown} comparison={data.comparison} compareLabel={compareLabel} bucket={bucket} />;
            }
            if (tab === "evolucion") {
              return <EvolucionTab current={shown} comparison={data.comparison} compareMode={data.compareMode} bucket={bucket} />;
            }
            if (tab === "horarios") return <HorariosTab current={shown} />;
            if (tab === "turnos") return <TurnosTab current={shown} />;
            return <CanchasTab current={shown} />;
          }}
        </StatsTabs>
      </StatsTooltipProvider>
    </div>
  );
}
